/**
 * ProductionLogger — structured JSON logger for production.
 *
 * Emits one JSON object per log entry to console.stdout,
 * suitable for log aggregation services (Datadog, Logtail,
 * CloudWatch, etc.). Sanitizes sensitive data before output.
 */

import type { Logger, LogLevel, LogContext, LogEntry } from './types';
import { sanitizeContext } from './sanitize';

export class ProductionLogger implements Logger {
  debug(message: string, context?: LogContext): void {
    this.emit('debug', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.emit('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.emit('warn', message, context);
  }

  error(message: string, context?: LogContext): void {
    this.emit('error', message, context);
  }

  audit(message: string, context?: LogContext): void {
    this.emit('audit', message, context);
  }

  private emit(level: LogLevel, message: string, context?: LogContext): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...this.defaultContext(),
        ...(context ? sanitizeContext(context as Record<string, unknown>) : {}),
      },
    };
    console.log(JSON.stringify(entry));
  }

  private defaultContext(): LogContext {
    return {
      browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };
  }
}
